export class InputValidator {
  // Much more targeted SQL injection patterns - won't trigger on normal text
  static SQL_INJECTION_PATTERNS = [
    // Database structure commands - more specific
    /(\bDROP\s+(TABLE|DATABASE|SCHEMA|INDEX|VIEW|PROCEDURE)\b)/gi,
    /(\bDELETE\s+FROM\s+\w+)/gi, // Only DELETE FROM, not just "delete"
    /(\bTRUNCATE\s+(TABLE\s+)?\w+)/gi,
    /(\bALTER\s+(TABLE|DATABASE|SCHEMA|INDEX|VIEW)\b)/gi,
    /(\bCREATE\s+(TABLE|DATABASE|SCHEMA|INDEX|VIEW|PROCEDURE)\b)/gi, // Only SQL CREATE commands
    
    // Execution commands with parentheses
    /(\b(EXEC|EXECUTE)\s*\([^)]*\))/gi,
    
    // Data manipulation - more specific patterns
    /(\bINSERT\s+INTO\s+\w+.*VALUES\s*\()/gi,
    /(\bUPDATE\s+\w+\s+SET\s+\w+\s*=)/gi,
    /(\bSELECT\s+[*\w,\s]+\s+FROM\s+\w+)/gi,
    /(\bUNION\s+(ALL\s+)?SELECT\b)/gi,
    
    // SQL comments and dangerous patterns
    /(--[^\r\n]*)/g,
    /(\/\*.*?\*\/)/gs,
    /(\bxp_\w+|\bsp_\w+)\s*\(/gi,
    /(\b(WAITFOR|DELAY)\s+['"]\d)/gi,
    
    // Chained commands
    /(;\s*(DROP\s+(TABLE|DATABASE)|DELETE\s+FROM|TRUNCATE|ALTER\s+(TABLE|DATABASE)|CREATE\s+(TABLE|DATABASE)|INSERT\s+INTO|UPDATE\s+\w+\s+SET|SELECT\s+.*\s+FROM))/gi,
    
    // Common injection patterns
    /(1\s*=\s*1|1\s*=\s*0)\s*(AND|OR|--)/gi,
    /('\s*(OR|AND)\s*'\s*=\s*')/gi,
    /(\bOR\s+1\s*=\s*1|\bAND\s+1\s*=\s*1)/gi,
    /('\s*;\s*(DROP|DELETE|INSERT|UPDATE|SELECT))/gi,
  ];

  static XSS_PATTERNS = [
    /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
    /<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi,
    /javascript\s*:/gi,
    /on(error|load|click|mouseover|mouseout|focus|blur|submit|change)\s*=/gi,
    /<[^>]*\s(onerror|onload|onclick|onmouseover)[\s]*=/gi,
    /expression\s*\(/gi,
    /vbscript\s*:/gi,
  ];

  // Improved SQL injection detection with fewer false positives
  static containsSqlInjection(input) {
    if (!input || typeof input !== 'string') return false;
    
    // Skip validation for URLs and image data
    if (this.isUrl(input) || this.isImageData(input)) {
      return false;
    }
    
    // Skip validation for very short texts
    if (input.length < 10) {
      return false;
    }
    
    // Check for dangerous patterns that should always be flagged
    const alwaysDangerousPatterns = [
      /(--[^\r\n]*)/g, // SQL comments
      /(\/\*.*?\*\/)/gs, // Block comments
      /('\s*(OR|AND)\s*'\s*=\s*')/gi, // Quote-based injections
      /(\bOR\s+1\s*=\s*1|\bAND\s+1\s*=\s*1)/gi, // Boolean injections
      /(;\s*DROP\s+(TABLE|DATABASE))/gi, // Drop commands
      /('\s*;\s*(DROP|DELETE|INSERT|UPDATE|SELECT))/gi, // Quote + command
    ];
    
    if (alwaysDangerousPatterns.some(pattern => pattern.test(input))) {
      return true;
    }
    
    // For other patterns, require multiple matches to reduce false positives
    const matchingPatterns = this.SQL_INJECTION_PATTERNS.filter(pattern => pattern.test(input));
    
    // Only flag if we have strong evidence (multiple patterns or very specific ones)
    return matchingPatterns.length >= 2;
  }

  // Check if input contains potential XSS
  static containsXSS(input) {
    if (!input || typeof input !== 'string') return false;
    
    // Skip validation for URLs and image data
    if (this.isUrl(input) || this.isImageData(input)) {
      return false;
    }
    
    return this.XSS_PATTERNS.some(pattern => pattern.test(input));
  }

  // Helper to check if a string is a URL
  static isUrl(string) {
    if (!string || typeof string !== 'string') return false;
    
    try {
      new URL(string);
      return true;
    } catch {
      // Also check for common URL patterns
      return /^(https?:\/\/|\/\/|data:|blob:)/.test(string);
    }
  }

  // Helper to check if string is image data (base64, blob, etc.)
  static isImageData(string) {
    if (!string || typeof string !== 'string') return false;
    
    return /^(data:image\/|blob:|https?:\/\/.*\.(jpg|jpeg|png|gif|webp|svg))/i.test(string);
  }

  // Check if input contains any malicious patterns
  static containsMaliciousContent(input) {
    return this.containsSqlInjection(input) || this.containsXSS(input);
  }

  // More conservative text sanitization
  static sanitizeText(input, maxLength = 1000, shouldTrim = false) {
    if (!input || typeof input !== 'string') return '';

    let sanitized = shouldTrim ? input.trim() : input;
    
    // Only remove if it's clearly malicious SQL/XSS
    if (this.containsMaliciousContent(sanitized)) {
      // Remove specific malicious patterns instead of all content
      this.SQL_INJECTION_PATTERNS.forEach(pattern => {
        sanitized = sanitized.replace(pattern, '');
      });
      
      this.XSS_PATTERNS.forEach(pattern => {
        sanitized = sanitized.replace(pattern, '');
      });
    }
    
    // Only encode if we detect actual XSS attempts, not normal text
    if (/<[^>]*script|<[^>]*on\w+\s*=/i.test(sanitized)) {
      sanitized = sanitized
        .replace(/[<>&"']/g, (char) => {
          const entityMap = {
            '<': '&lt;',
            '>': '&gt;',
            '&': '&amp;',
            '"': '&quot;',
            "'": '&#x27;'
          };
          return entityMap[char] || char;
        });
    }
    
    // Limit length to prevent buffer overflow attacks
    return sanitized.substring(0, maxLength);
  }

  // Sanitize email input
  static sanitizeEmail(email) {
    if (!email || typeof email !== 'string') return '';
    
    let sanitized = email.trim().toLowerCase();
    
    // Remove SQL injection patterns
    if (this.containsSqlInjection(sanitized)) {
      this.SQL_INJECTION_PATTERNS.forEach(pattern => {
        sanitized = sanitized.replace(pattern, '');
      });
    }
    
    // Remove any characters that aren't valid in emails
    sanitized = sanitized.replace(/[^a-z0-9._%+-@]/g, '');
    
    // Basic email validation pattern
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    
    // Return empty string if doesn't match email pattern
    return emailPattern.test(sanitized) ? sanitized : '';
  }

  // Sanitize numeric input
  static sanitizeNumber(input) {
    if (typeof input === 'number') return input;
    if (!input || typeof input !== 'string') return 0;
    
    // Remove all non-numeric characters except decimal point and minus sign
    const sanitized = input.replace(/[^0-9.-]/g, '');
    const number = parseFloat(sanitized);
    
    return isNaN(number) ? 0 : number;
  }

  // Sanitize phone number
  static sanitizePhone(phone) {
    if (!phone || typeof phone !== 'string') return '';
    
    // Remove SQL injection patterns first
    let sanitized = phone.trim();
    if (this.containsSqlInjection(sanitized)) {
      this.SQL_INJECTION_PATTERNS.forEach(pattern => {
        sanitized = sanitized.replace(pattern, '');
      });
    }
    
    // Keep only numbers, spaces, dashes, parentheses, and plus sign
    sanitized = sanitized.replace(/[^0-9\s\-()+]/g, '');
    
    return sanitized.substring(0, 20); // Reasonable phone number length
  }

  // Enhanced URL sanitization - more permissive for legitimate URLs
  static sanitizeUrl(url) {
    if (!url || typeof url !== 'string') return '';
    
    let sanitized = url.trim();
    
    // Only remove SQL injection if detected
    if (this.containsSqlInjection(sanitized)) {
      this.SQL_INJECTION_PATTERNS.forEach(pattern => {
        sanitized = sanitized.replace(pattern, '');
      });
    }
    
    // Remove dangerous protocols
    if (sanitized.match(/^(javascript|data:text|vbscript):/i)) {
      return '';
    }
    
    // Allow data URLs for images
    if (sanitized.startsWith('data:image/')) {
      return sanitized; // Return as-is for image data URLs
    }
    
    // Allow blob URLs
    if (sanitized.startsWith('blob:')) {
      return sanitized;
    }
    
    // Basic URL validation - add https if missing protocol
    if (!sanitized.match(/^https?:\/\//)) {
      sanitized = 'https://' + sanitized;
    }
    
    try {
      const urlObj = new URL(sanitized);
      return urlObj.href;
    } catch {
      return '';
    }
  }

  // Sanitize password - minimal changes to preserve password integrity
  static sanitizePassword(input) {
    if (!input || typeof input !== 'string') return '';
    
    // For passwords, only check for obvious injection attempts
    if (this.containsMaliciousContent(input)) {
      throw new Error('Invalid password format');
    }
    
    return input; // Don't trim passwords as spaces might be intentional
  }

  // Sanitize OTP code
  static sanitizeOtp(input) {
    if (!input || typeof input !== 'string') return '';
    
    // OTP should only contain numbers
    const sanitized = input.replace(/[^0-9]/g, '');
    return sanitized.substring(0, 10); // Reasonable OTP length
  }

  // Enhanced validation with better error messages
  static validateAndSanitize(value, fieldType, fieldName = 'field', options = {}) {
    const { shouldTrim = false } = options;
    const result = {
      isValid: true,
      sanitizedValue: value,
      errorMessage: null
    };

    try {
      // Skip validation for empty values unless required
      if (!value || value === '') {
        result.sanitizedValue = '';
        return result;
      }

      // Check for malicious content with exceptions
      if (fieldType !== 'url' && fieldType !== 'image' && this.containsMaliciousContent(value)) {
        result.isValid = false;
        result.errorMessage = `Invalid characters detected in ${fieldName}. Please remove any special characters or code.`;
        result.sanitizedValue = '';
        return result;
      }

      // Sanitize based on field type
      switch (fieldType) {
        case 'email':
          result.sanitizedValue = this.sanitizeEmail(value);
          if (value && !result.sanitizedValue) {
            result.isValid = false;
            result.errorMessage = `Please enter a valid email address for ${fieldName}.`;
          }
          break;

        case 'phone':
          result.sanitizedValue = this.sanitizePhone(value);
          break;

        case 'url':
        case 'image':
          result.sanitizedValue = this.sanitizeUrl(value);
          if (value && !result.sanitizedValue) {
            result.isValid = false;
            result.errorMessage = `Please enter a valid URL for ${fieldName}.`;
          }
          break;

        case 'number':
          result.sanitizedValue = this.sanitizeNumber(value);
          break;

        case 'password':
          result.sanitizedValue = this.sanitizePassword(value);
          break;

        case 'otp':
          result.sanitizedValue = this.sanitizeOtp(value);
          break;

        case 'textarea':
          result.sanitizedValue = this.sanitizeText(value, 5000, shouldTrim); // Longer limit for textarea
          break;

        case 'text':
        default:
          result.sanitizedValue = this.sanitizeText(value, 1000, shouldTrim);
          break;
      }
    } catch (error) {
      result.isValid = false;
      result.errorMessage = error.message;
      result.sanitizedValue = '';
    }

    return result;
  }

  // Enhanced object sanitization with better array handling
  static sanitizeObject(obj, fieldTypes = {}) {
    if (!obj || typeof obj !== 'object') return { sanitized: {}, errors: [] };
    
    const sanitized = {};
    const errors = [];
    
    Object.keys(obj).forEach(key => {
      const value = obj[key];
      const fieldType = fieldTypes[key] || 'text';
      
      if (value !== null && value !== undefined) {
        // Handle arrays (like profileFields)
        if (Array.isArray(value)) {
          sanitized[key] = value.map((item, index) => {
            if (typeof item === 'object' && item !== null) {
              const itemResult = this.sanitizeObject(item, fieldTypes);
              if (itemResult.errors.length > 0) {
                itemResult.errors.forEach(error => {
                  errors.push({
                    field: `${key}[${index}].${error.field}`,
                    message: error.message
                  });
                });
              }
              return itemResult.sanitized;
            } else {
              const validation = this.validateAndSanitize(item, fieldType, `${key}[${index}]`);
              if (!validation.isValid) {
                errors.push({
                  field: `${key}[${index}]`,
                  message: validation.errorMessage
                });
              }
              return validation.sanitizedValue;
            }
          });
        } else if (typeof value === 'object') {
          const nestedResult = this.sanitizeObject(value, fieldTypes);
          sanitized[key] = nestedResult.sanitized;
          nestedResult.errors.forEach(error => {
            errors.push({
              field: `${key}.${error.field}`,
              message: error.message
            });
          });
        } else {
          const validation = this.validateAndSanitize(value, fieldType, key);
          sanitized[key] = validation.sanitizedValue;
          
          if (!validation.isValid) {
            errors.push({
              field: key,
              message: validation.errorMessage
            });
          }
        }
      } else {
        sanitized[key] = value;
      }
    });
    
    return { sanitized, errors };
  }
}

// Legacy compatibility - matches your existing sanitizeInput structure
export const sanitizeInput = {
  text: (input, shouldTrim = true) => InputValidator.sanitizeText(input, 1000, shouldTrim),
  email: (input) => InputValidator.sanitizeEmail(input),
  phone: (input) => InputValidator.sanitizePhone(input),
  url: (input) => InputValidator.sanitizeUrl(input),
  number: (input) => InputValidator.sanitizeNumber(input),
  password: (input) => InputValidator.sanitizePassword(input),
  otp: (input) => InputValidator.sanitizeOtp(input),
};